import { InvestorPositionPanel } from '../components/InvestorPositionPanel';
import { makeReconRowFilter } from '../utils/reconFilters';
import React from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../../components/ui/card';
import { Button } from '../../../../components/ui/button';
import { Input } from '../../../../components/ui/input';
import { Badge } from '../../../../components/ui/badge';
import { Skeleton } from '../../../../components/ui/skeleton';
import { TabsContent } from '../../../../components/ui/tabs';
import { InvestorPicker } from '../../../../components/InvestorPicker';
import { WipeInvestorButton } from '../../../../components/WipeInvestorButton';
import { WipeAllUploadedButton } from '../../../../components/WipeAllUploadedButton';
import { AutoMatchAuditPanel } from '../../../../components/AutoMatchAuditPanel';
import { BadMatchesButton } from '../components/BadMatchesButton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../../components/ui/table';
import {
  DollarSign,
  Upload,
  Search,
  RefreshCw,
  Trash2,
  X,
  CheckCircle,
  XCircle,
  AlertTriangle,
  FileText,
  Users,
  Link,
  Check,
  ChevronsUpDown,
} from 'lucide-react';
import {
  API_URL,
  TRANSACTION_TYPES,
  getTransactionConfig,
  formatCurrency,
  formatDate,
} from '../utils/formatters';
import { SortableHeader, sortItems } from '../utils/sorting';

export function ReconciliationTabPanel({ ctx }) {
  const {
    headers,
    reconData, setReconData,
    reconLoading,
    reconInvestorFilter, setReconInvestorFilter,
    reconIncludeUntagged, setReconIncludeUntagged,
    reconFromDate, setReconFromDate,
    reconToDate, setReconToDate,
    reconSearch, setReconSearch,
    reconInvestors,
    reconClearIds,
    selectedRecordedTxn, setSelectedRecordedTxn,
    selectedBankTxn, setSelectedBankTxn,
    setMatchConfirmOpen,
    selectedReconRecorded, setSelectedReconRecorded,
    selectedReconBank, setSelectedReconBank,
    reconBulkDeleting,
    selectedMatched, setSelectedMatched,
    toggleMatched,
    expandedMatched, setExpandedMatched,
    showMatched, setShowMatched,
    showSuggestions, setShowSuggestions,
    showUnmatchedUploaded, setShowUnmatchedUploaded,
    showUnmatchedBank, setShowUnmatchedBank,
    suggestionBusyId,
    multiAllocLoading,
    sortRecon, setSortRecon,
    sortReconRecorded, setSortReconRecorded,
    sortReconBank, setSortReconBank,
    toggleSort,
    fetchReconciliation,
    runDedupe,
    runFixAllocatedCashflows,
    runPromoteConfirmedStaging,
    handleAllocateMultiBank,
    handleReconBulkUnmatch,
    handleReconBulkVerifyRecorded,
    handleReconBulkDeleteRecorded,
    handleReconBulkVerifyBank,
    handleReconBulkDeleteBank,
    handleReconBulkForceCleanRecorded,
    handleReconBulkForceCleanBank,
    handleResplitMatched,
    handleUndoVerifyNoMatch,
    handleUnmatch,
    handleOpenReverseAllocation,
    handleOpenSplit,
    handleVerifyNoMatch,
    handleOpenAllocation,
    handleOpenBankSplit,
    handleUnsplitBank,
    openInspect,
    openInspectRecorded,
    openManageSplits,
    acceptSuggestion,
    rejectSuggestion,
    selectedSuggestions, toggleSuggestionSelected, toggleAllSuggestionsSelected,
    bulkSuggestionLoading, bulkAcceptSuggestions, bulkRejectSuggestions,
    toggleReconRecorded,
    toggleReconBank,
    toggleAllReconRecorded,
    toggleAllReconBank,
    setReimportOpen, setReimportFile, setReimportResult,
    setReassignOpen,
  } = ctx;

  return (
    <TabsContent value="reconciliation" className="space-y-4 recon-big-checks" data-testid="reconciliation-tab">
      {(() => {
        // Client-side filter applied to ALL 3 sections (matched,
        // unmatched uploaded, unmatched bank) so admin can drill into
        // a specific reference / loan / investor / date range without
        // waiting on a server round-trip.
        const _q = (reconSearch || '').trim().toLowerCase();
        // Client-side date narrower — the backend already applies
        // reconFromDate / reconToDate, so this is only a safety net
        // for the matched section (which the backend returns as
        // pairs, not filtered by date on the bank side).
        const _fromDt = reconFromDate || '';
        const _toDt = reconToDate || '';
        const _inDateRange = (dStr) => {
          const d = String(dStr || '').slice(0, 10);
          if (!d) return true;
          if (_fromDt && d < _fromDt) return false;
          if (_toDt && d > _toDt) return false;
          return true;
        };
        // Recorded (uploaded) rows carry their date on `transaction_date`;
        // bank rows carry it on `posting_date` (sometimes `value_date`).
        // Reading only `transaction_date` here meant the date filter
        // silently passed EVERY bank row through unfiltered (empty date
        // -> _inDateRange treats it as "no date to exclude on").
        const _rowDate = (r) => r.transaction_date || r.posting_date || r.value_date || '';
        const _rowMatchesQ = (r) => {
          if (!_q) return true;
          return [
            r.reference, r.loan_id, r.description, r.notes,
            r.investor_name, r.investor_id, r.bank_description,
            r.transaction_type, r.amount, _rowDate(r),
          ].some(v => String(v ?? '').toLowerCase().includes(_q));
        };
        // Same predicate the allocation windows use (utils/reconFilters.js).
        const _filterFn = makeReconRowFilter({ search: reconSearch, fromDate: reconFromDate, toDate: reconToDate });

        // Matched section: each row is { recorded, bank } — search
        // hits either side; date range hits either side (the matched
        // pair passes if EITHER row's date is in range).
        const _matchedFilterFn = (m) => {
          const rec = m?.recorded || {};
          const bnk = m?.bank || {};
          if (_q) {
            const hitRec = [
              rec.reference, rec.loan_id, rec.description, rec.notes,
              rec.investor_name, rec.investor_id, rec.transaction_type,
              rec.amount, rec.transaction_date,
            ].some(v => String(v ?? '').toLowerCase().includes(_q));
            const hitBnk = [
              bnk.description, bnk.bank_description, bnk.reference,
              bnk.investor_name, bnk.investor_id, bnk.amount,
              bnk.posting_date, bnk.transaction_date,
            ].some(v => String(v ?? '').toLowerCase().includes(_q));
            if (!hitRec && !hitBnk) return false;
          }
          if (_fromDt || _toDt) {
            const recD = String(rec.transaction_date || '').slice(0, 10);
            const bnkD = String(bnk.posting_date || bnk.transaction_date || '').slice(0, 10);
            const recIn = recD && _inDateRange(recD);
            const bnkIn = bnkD && _inDateRange(bnkD);
            // pair passes if either row is in-range (or has no date to
            // exclude on)
            if (!recIn && !bnkIn) return false;
          }
          return true;
        };
        const filteredMatched = (reconData?.matched || []).filter(_matchedFilterFn);
        const filteredRecorded = sortItems((reconData?.unmatched_recorded || []).filter(_filterFn), sortReconRecorded);
        const filteredBank = sortItems((reconData?.unmatched_bank || []).filter(_filterFn), sortReconBank);

        // Sticky tally bar values — sum of selected uploaded + bank rows.
        // Signs preserved as-is (user spec). Difference = uploaded − bank
        // (user 2026-10-09; was the sum): 0.00 when the two sides balance.
        const _stickyUploadSum = (reconData?.unmatched_recorded || [])
          .filter(t => selectedReconRecorded.has(t.id))
          .reduce((acc, t) => acc + (parseFloat(t.amount) || 0), 0);
        const _stickyBankSum = (reconData?.unmatched_bank || [])
          .filter(b => selectedReconBank.has(b.id))
          .reduce((acc, b) => acc + (parseFloat(b.amount) || 0), 0);
        const _stickyShow = selectedReconRecorded.size > 0 || selectedReconBank.size > 0 || selectedMatched.size > 0;
        const _fmtSum = (n) => Number(n).toLocaleString('en-ZA', {
          minimumFractionDigits: 2, maximumFractionDigits: 2, signDisplay: 'always',
        });
        const _pillColor = (n) => n > 0 ? 'text-emerald-700 bg-emerald-50 border-emerald-300'
                                : n < 0 ? 'text-red-700 bg-red-50 border-red-300'
                                : 'text-slate-700 bg-slate-50 border-slate-300';
        return (
      <>
      {/* Sticky running-tally bar — pins to the top of the viewport while
          the admin scrolls through long uploaded/bank tables. Shows BOTH
          tallies plus the cross-side net so the user can balance a
          rebalance match in one glance. */}
      {_stickyShow && (
        <div
          className="sticky top-2 z-40 flex flex-wrap items-center gap-3 px-4 py-2 rounded-lg border border-slate-200 bg-white/95 backdrop-blur shadow-md"
          data-testid="recon-sticky-tally-bar"
        >
          <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">Running Σ</span>
          <div className={`flex items-center gap-1.5 px-3 py-1 rounded-md border text-sm font-mono ${_pillColor(_stickyUploadSum)}`} data-testid="recon-sticky-uploaded-tally">
            <span className="text-xs uppercase tracking-wide opacity-70">Uploaded ({selectedReconRecorded.size})</span>
            <span className="font-semibold">R {_fmtSum(_stickyUploadSum)}</span>
          </div>
          <div className={`flex items-center gap-1.5 px-3 py-1 rounded-md border text-sm font-mono ${_pillColor(_stickyBankSum)}`} data-testid="recon-sticky-bank-tally">
            <span className="text-xs uppercase tracking-wide opacity-70">Bank ({selectedReconBank.size})</span>
            <span className="font-semibold">R {_fmtSum(_stickyBankSum)}</span>
          </div>
          {selectedReconRecorded.size > 0 && selectedReconBank.size > 0 && (() => {
            const net = _stickyUploadSum - _stickyBankSum;
            const clean = Math.abs(net) < 0.005;
            return (
              <div
                className={`flex items-center gap-1.5 px-3 py-1 rounded-md border text-sm font-mono ${clean ? 'text-emerald-800 bg-emerald-100 border-emerald-400' : 'text-amber-800 bg-amber-100 border-amber-400'}`}
                data-testid="recon-sticky-net-tally"
                title="Uploaded − Bank — for a balanced manual match this should be 0.00"
              >
                <span className="text-xs uppercase tracking-wide opacity-70">Difference (Up−Bank)</span>
                <span className="font-semibold">{clean ? 'R 0.00 ✓' : `R ${_fmtSum(net)}`}</span>
              </div>
            );
          })()}
          {/* Bulk-edit transaction_type on any selected rows (matched or
              unmatched, uploaded or bank). Backend routes each id to the
              collection it lives in and returns per-id outcomes. */}
          <BulkEditTypeControl
            ctx={ctx}
            selectedReconRecorded={selectedReconRecorded}
            selectedReconBank={selectedReconBank}
            selectedMatched={selectedMatched}
            fetchReconciliation={fetchReconciliation}
            reconInvestorFilter={reconInvestorFilter}
            reconFromDate={reconFromDate}
            reconToDate={reconToDate}
            setSelectedReconRecorded={setSelectedReconRecorded}
            setSelectedReconBank={setSelectedReconBank}
            setSelectedMatched={setSelectedMatched}
          />
          <button
            className="ml-auto text-xs text-slate-500 hover:text-slate-700 px-2 py-1 rounded hover:bg-slate-100"
            onClick={() => { setSelectedReconRecorded(new Set()); setSelectedReconBank(new Set()); setSelectedMatched(new Set()); }}
            data-testid="recon-sticky-clear-selections"
          >
            Clear selections
          </button>
        </div>
      )}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <CardTitle className="text-lg">Transaction Reconciliation</CardTitle>
              <CardDescription>Match uploaded transactions against bank movements</CardDescription>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <Button
                variant="outline"
                size="sm"
                className="border-amber-300 text-amber-700 hover:bg-amber-50"
                data-testid="recon-toolbar-dedupe-uploaded"
                title="Find and remove duplicate UPLOADED (csv_import) rows across matched + unmatched sections. Rule: same date + same type + same amount = duplicate. Allocated/verified rows are preserved as survivors."
                onClick={() => runDedupe('uploaded')}
              >
                <Trash2 className="h-4 w-4 mr-1" /> Dedupe Uploaded
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="border-amber-300 text-amber-700 hover:bg-amber-50"
                data-testid="recon-toolbar-dedupe-bank"
                title="Find and remove duplicate BANK rows across matched + unmatched sections. Rule: same date + same amount + same description = duplicate. Allocated/verified rows are preserved as survivors."
                onClick={() => runDedupe('bank')}
              >
                <Trash2 className="h-4 w-4 mr-1" /> Dedupe Bank
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="border-indigo-300 text-indigo-700 hover:bg-indigo-50"
                data-testid="recon-toolbar-fix-allocated-cashflows"
                title="Find deal_purchase/deal_repayment cashflows that a real, intact allocation already covers but whose reconciliation_status was never written directly onto the row - invisible to the General Ledger, statements and dashboard until fixed. Does not touch or create any allocation."
                onClick={runFixAllocatedCashflows}
              >
                <RefreshCw className="h-4 w-4 mr-1" /> Fix Allocated Cashflows
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="border-indigo-300 text-indigo-700 hover:bg-indigo-50"
                data-testid="recon-toolbar-promote-confirmed-staging"
                title="Find bank transactions (Investec sync) that were already matched or verified here in Reconciliation, but never committed into the real ledger because they were still sitting in the staging area - invisible to the GL/cash balance until fixed. Commits them now, using the same reconciliation decision already made."
                onClick={runPromoteConfirmedStaging}
              >
                <RefreshCw className="h-4 w-4 mr-1" /> Promote Reconciled Staging
              </Button>
              <BadMatchesButton
                onDone={() => reconInvestorFilter && reconInvestorFilter !== 'all'
                  && fetchReconciliation(reconInvestorFilter, reconFromDate, reconToDate)}
              />
              <InvestorPicker
                value={reconInvestorFilter || ''}
                onChange={(id) => {
                  // No "all investors" option here — an unfiltered load
                  // pulls the entire book (42k+ transactions / 85k+
                  // cashflows in production) and used to hang the whole
                  // backend for minutes. An investor selection is required.
                  const next = id || '';
                  setReconInvestorFilter(next);
                  if (next) {
                    fetchReconciliation(next, reconFromDate, reconToDate);
                  } else {
                    setReconData(null);
                  }
                }}
                investors={reconInvestors}
                clearIds={reconClearIds}
                includeAll={false}
                placeholder="Make a selection"
                className="w-64"
                testId="recon-investor-filter"
              />
              <Input type="date" value={reconFromDate} onChange={e => setReconFromDate(e.target.value)} className="w-36" placeholder="From" data-testid="recon-from-date" />
              <Input type="date" value={reconToDate} onChange={e => setReconToDate(e.target.value)} className="w-36" placeholder="To" data-testid="recon-to-date" />
              <label
                className="flex items-center gap-1.5 text-xs text-slate-600 whitespace-nowrap cursor-pointer select-none"
                title="Also show bank rows that have no investor tag (e.g. Investec imports whose description didn't match an RP pattern). Only applies when filtering by a specific investor."
                data-testid="recon-include-untagged-label"
              >
                <input
                  type="checkbox"
                  checked={reconIncludeUntagged}
                  onChange={e => {
                    const next = e.target.checked;
                    setReconIncludeUntagged(next);
                    if (reconInvestorFilter) {
                      fetchReconciliation(reconInvestorFilter === 'all' ? '' : reconInvestorFilter, reconFromDate, reconToDate, next);
                    }
                  }}
                  data-testid="recon-include-untagged-checkbox"
                />
                Show untagged bank rows
              </label>
              <WipeInvestorButton
                API_URL={API_URL}
                headers={headers}
                investorId={reconInvestorFilter && reconInvestorFilter !== 'all' ? reconInvestorFilter : ''}
                onComplete={() => fetchReconciliation(reconInvestorFilter === 'all' ? '' : reconInvestorFilter, reconFromDate, reconToDate)}
              />
              <WipeAllUploadedButton
                API_URL={API_URL}
                headers={headers}
                onComplete={() => fetchReconciliation(reconInvestorFilter === 'all' ? '' : reconInvestorFilter, reconFromDate, reconToDate)}
              />
              <Button
                size="sm"
                variant="outline"
                className="border-amber-300 text-amber-700 hover:bg-amber-50"
                onClick={openManageSplits}
                data-testid="manage-splits-btn"
                title="View every split parent + unsplit any that are stuck"
              >
                Manage Splits
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                data-testid="rerun-auto-match-btn"
                title={
                  reconInvestorFilter && reconInvestorFilter !== 'all'
                    ? `Re-enable auto-match on all unmatched rows for ${reconInvestorFilter} and refresh`
                    : 'Re-enable auto-match across ALL investors and refresh'
                }
                onClick={async () => {
                  const inv = reconInvestorFilter && reconInvestorFilter !== 'all' ? reconInvestorFilter : null;
                  try {
                    const r = await axios.post(
                      `${API_URL}/api/admin/investor-transactions/reconciliation/reenable-auto-match-bulk`,
                      { investor_id: inv, only_unmatched: true },
                      { headers }
                    );
                    toast.success(r.data?.message || `Re-enabled ${r.data?.total_modified || 0} row(s)`);
                    fetchReconciliation(reconInvestorFilter === 'all' ? '' : reconInvestorFilter, reconFromDate, reconToDate);
                  } catch (err) {
                    toast.error(err.response?.data?.detail || 'Re-enable failed');
                  }
                }}
              >
                Re-run Auto-Match
              </Button>
              <div className="relative">
                <Search className="h-3 w-3 text-gray-400 absolute left-2 top-1/2 -translate-y-1/2 pointer-events-none" />
                <Input
                  type="text"
                  value={reconSearch}
                  onChange={e => setReconSearch(e.target.value)}
                  placeholder="Search reference, loan, description…"
                  className="w-64 pl-7"
                  data-testid="recon-search"
                />
                {reconSearch && (
                  <button
                    onClick={() => setReconSearch('')}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    title="Clear search"
                    data-testid="recon-search-clear"
                  >
                    <X className="h-3 w-3" />
                  </button>
                )}
              </div>
              <Button variant="outline" size="sm" onClick={() => fetchReconciliation(reconInvestorFilter === 'all' ? '' : reconInvestorFilter, reconFromDate, reconToDate)} data-testid="recon-refresh"><RefreshCw className="h-4 w-4 mr-1" /> Refresh</Button>
              <Button
                size="sm"
                className="bg-amber-500 hover:bg-amber-600 text-slate-900"
                onClick={() => { setReimportOpen(true); setReimportFile(null); setReimportResult(null); }}
                data-testid="reimport-excel-btn"
                title="Upload the latest examplefile.xlsb and refresh expected RP/LB cashflows. Existing CSV transactions and bank allocations are preserved; orphaned cashflow allocations are smart-rebound within ±2 days and ±R0.01."
              >
                <Upload className="h-4 w-4 mr-1" /> Re-import Excel &amp; Replay
              </Button>
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Manual Match Selection Banner */}
      {(selectedRecordedTxn || selectedBankTxn) && (
        <Card className="border-blue-300 bg-blue-50">
          <CardContent className="p-4">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-4 text-sm">
                <Link className="h-5 w-5 text-blue-600" />
                <div>
                  <span className="font-medium text-blue-800">Manual Match Mode</span>
                  <span className="text-blue-600 ml-2">
                    {selectedRecordedTxn && !selectedBankTxn && selectedReconBank.size === 0 && 'Now select a bank transaction to match with, OR tick multiple bank checkboxes to allocate them all'}
                    {selectedRecordedTxn && !selectedBankTxn && selectedReconBank.size > 0 && `${selectedReconBank.size} bank txn(s) ticked — ready to allocate to this recorded`}
                    {!selectedRecordedTxn && selectedBankTxn && 'Now select a recorded transaction to match with'}
                    {selectedRecordedTxn && selectedBankTxn && 'Ready to match — review and confirm'}
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {selectedRecordedTxn && !selectedBankTxn && selectedReconBank.size > 0 && (
                  <Button
                    size="sm"
                    onClick={handleAllocateMultiBank}
                    disabled={multiAllocLoading}
                    data-testid="recon-allocate-multi-bank"
                    className="bg-indigo-600 hover:bg-indigo-700 text-white"
                  >
                    <DollarSign className="h-4 w-4 mr-1" />
                    {multiAllocLoading ? 'Allocating…' : `Allocate ${selectedReconBank.size} Bank(s) → Recorded`}
                  </Button>
                )}
                {selectedRecordedTxn && selectedBankTxn && (
                  <Button size="sm" onClick={() => setMatchConfirmOpen(true)} data-testid="recon-confirm-match" className="bg-green-600 hover:bg-green-700 text-white">
                    <Check className="h-4 w-4 mr-1" /> Confirm Match
                  </Button>
                )}
                <Button size="sm" variant="outline" onClick={() => { setSelectedRecordedTxn(null); setSelectedBankTxn(null); }} data-testid="recon-cancel-match">
                  <X className="h-4 w-4 mr-1" /> Cancel
                </Button>
              </div>
            </div>
            {selectedRecordedTxn && !selectedBankTxn && selectedReconBank.size > 0 && (
              <div className="mt-3 text-xs bg-white rounded p-3 border border-indigo-200">
                <p className="text-indigo-700 font-medium mb-1">Allocation Preview</p>
                <p className="text-slate-700">
                  <strong>Recorded:</strong> {selectedRecordedTxn.investor_name} — {formatCurrency(selectedRecordedTxn.amount)}
                  {selectedRecordedTxn.allocated_amount > 0 && (
                    <span className="text-indigo-600 ml-2">(already allocated {formatCurrency(selectedRecordedTxn.allocated_amount)}, remaining {formatCurrency(selectedRecordedTxn.remaining_to_allocate)})</span>
                  )}
                </p>
                <p className="text-slate-700 mt-1">
                  <strong>Banks selected:</strong> {selectedReconBank.size} • Total available {formatCurrency(
                    (reconData?.unmatched_bank || [])
                      .filter(b => selectedReconBank.has(b.id))
                      .reduce((s, b) => s + (b.remaining_balance != null ? Number(b.remaining_balance) : Math.abs(Number(b.amount) || 0)), 0)
                  )}
                </p>
              </div>
            )}
            {selectedRecordedTxn && selectedBankTxn && (
              <div className="grid grid-cols-2 gap-4 mt-3 text-sm">
                <div className="bg-white rounded p-3 border border-amber-200">
                  <p className="text-xs text-amber-600 font-medium mb-1">Recorded Transaction</p>
                  <p>{selectedRecordedTxn.investor_name}: {formatCurrency(selectedRecordedTxn.amount)}</p>
                  <p className="text-gray-500">{formatDate(selectedRecordedTxn.transaction_date)}</p>
                </div>
                <div className="bg-white rounded p-3 border border-red-200">
                  <p className="text-xs text-red-600 font-medium mb-1">Bank Transaction</p>
                  <p>{selectedBankTxn.description || 'No description'}: {formatCurrency(selectedBankTxn.amount)}</p>
                  <p className="text-gray-500">{formatDate(selectedBankTxn.posting_date || selectedBankTxn.value_date)}</p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {reconLoading ? (
        <div className="space-y-3">{[1,2,3].map(i => <Skeleton key={i} className="h-16 w-full" />)}</div>
      ) : !reconData ? (
        <Card className="border-dashed border-slate-300" data-testid="recon-empty-state">
          <CardContent className="py-16 text-center text-slate-500 space-y-3">
            <Search className="h-10 w-10 text-slate-300 mx-auto" />
            <div className="text-sm font-medium text-slate-700">No data loaded</div>
            <div className="text-xs">Pick an investor from the dropdown above to load their reconciliation data.</div>
            <div className="text-xs text-slate-400">Tip: search by investor name <em>or</em> RP code (e.g. <span className="font-mono">18-00</span>).</div>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Investor's position on a chosen date (user 2026-10-13) */}
          <InvestorPositionPanel
            headers={headers}
            investorId={reconInvestorFilter && reconInvestorFilter !== 'all' ? reconInvestorFilter : ''}
            refreshKey={reconData}
          />
          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <Card><CardContent className="p-4 text-center"><p className="text-xs text-gray-500">Uploaded</p><p className="text-xl font-bold" data-testid="recon-total-recorded">{reconData.summary.total_recorded}</p></CardContent></Card>
            <Card><CardContent className="p-4 text-center"><p className="text-xs text-gray-500">Bank Movements</p><p className="text-xl font-bold" data-testid="recon-total-bank">{reconData.summary.total_bank}</p></CardContent></Card>
            <Card className="border-green-200"><CardContent className="p-4 text-center"><p className="text-xs text-green-600">Matched</p><p className="text-xl font-bold text-green-700" data-testid="recon-matched">{reconData.summary.matched_count}</p></CardContent></Card>
            <Card className="border-amber-200"><CardContent className="p-4 text-center"><p className="text-xs text-amber-600">Unmatched Uploaded</p><p className="text-xl font-bold text-amber-700" data-testid="recon-unmatched-recorded">{reconData.summary.unmatched_recorded_count}</p></CardContent></Card>
            <Card className="border-red-200"><CardContent className="p-4 text-center"><p className="text-xs text-red-600">Unmatched Bank</p><p className="text-xl font-bold text-red-700" data-testid="recon-unmatched-bank">{reconData.summary.unmatched_bank_count}</p></CardContent></Card>
          </div>

          {/* Matched Transactions — collapsed by default to reduce scrolling */}
          {(reconData.matched || []).length > 0 && (
            <Card>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between gap-2">
                  <button
                    type="button"
                    onClick={() => setShowMatched(s => !s)}
                    className="flex-1 flex items-center justify-between text-sm font-semibold text-slate-700 hover:text-slate-900 transition"
                    data-testid="recon-matched-toggle"
                  >
                    <span className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-green-600" />
                      Matched Transactions ({filteredMatched.length}{(_q || _fromDt || _toDt) && filteredMatched.length !== reconData.matched.length ? ` of ${reconData.matched.length}` : ''})
                    </span>
                    <span className="flex items-center gap-1 text-xs text-slate-500">
                      {showMatched ? 'Hide' : 'Show'}
                      <ChevronsUpDown className="h-3 w-3" />
                    </span>
                  </button>
                  {showMatched && selectedMatched.size > 0 && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={handleReconBulkUnmatch}
                      disabled={reconBulkDeleting}
                      className="text-amber-700 border-amber-300 hover:bg-amber-50"
                      data-testid="recon-bulk-unmatch"
                    >
                      <X className="h-3 w-3 mr-1" />
                      {reconBulkDeleting ? 'Working...' : `Unmatch ${selectedMatched.size} Selected`}
                    </Button>
                  )}
                </div>
              </CardHeader>
              {showMatched && (
              <CardContent>
                {(() => {
                  const sortedMatched = sortItems(filteredMatched, sortRecon);
                  // FIX: verified-no-match bank rows have no recorded.id —
                  // fall back to bank.id same as the per-row checkbox so
                  // Select All actually selects EVERYTHING, including
                  // verified bank entries.
                  const allOnPage = sortedMatched
                    .map(m => m.recorded?.id || m.bank?.id)
                    .filter(Boolean);
                  const allChecked = allOnPage.length > 0 && allOnPage.every(id => selectedMatched.has(id));
                  return (
                <Table>
                  <TableHeader><TableRow className="bg-green-50">
                    <TableHead className="w-10">
                      <input
                        type="checkbox"
                        checked={allChecked}
                        onChange={() => setSelectedMatched(allChecked ? new Set() : new Set(allOnPage))}
                        className="rounded"
                        data-testid="recon-matched-select-all"
                      />
                    </TableHead>
                    <SortableHeader label="Investor" sortKey="recorded.investor_name" sortState={sortRecon} onSort={toggleSort(setSortRecon)} />
                    <SortableHeader label="Date (Recorded)" sortKey="recorded.transaction_date" sortState={sortRecon} onSort={toggleSort(setSortRecon)} />
                    <SortableHeader label="Type" sortKey="recorded.transaction_type" sortState={sortRecon} onSort={toggleSort(setSortRecon)} />
                    <SortableHeader label="Amount (Recorded)" sortKey="recorded.amount" sortState={sortRecon} onSort={toggleSort(setSortRecon)} className="text-right" />
                    <SortableHeader label="Date (Bank)" sortKey="bank.posting_date" sortState={sortRecon} onSort={toggleSort(setSortRecon)} />
                    <SortableHeader label="Amount (Bank)" sortKey="bank.amount" sortState={sortRecon} onSort={toggleSort(setSortRecon)} className="text-right" />
                    <TableHead>Quality</TableHead><TableHead></TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {sortedMatched.map((m, i) => {
                      // For verified-bank rows the recorded side is synthetic and has no
                      // id — fall back to bank.id so the checkbox/unmatch still work.
                      const rid = m.recorded?.id || m.bank?.id;
                      const undoId = m.recorded?.id || m.bank?.id;
                      const expanded = expandedMatched.has(rid);
                      const existing = m.recorded?.existing_allocations || [];
                      const hasDetails = existing.length > 0 || m.match_quality === 'verified_no_match' || m.recorded?.reference || m.recorded?.notes || m.recorded?.loan_id || m.bank?.description;
                      return (
                      <React.Fragment key={`${rid || 'm'}-${i}`}>
                      <TableRow data-testid={`recon-matched-${i}`} className={rid && selectedMatched.has(rid) ? 'bg-amber-50' : ''}>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            {rid && (
                              <input
                                type="checkbox"
                                checked={selectedMatched.has(rid)}
                                onChange={() => toggleMatched(rid)}
                                className="rounded"
                                data-testid={`recon-matched-checkbox-${i}`}
                              />
                            )}
                            {hasDetails && (
                              <button
                                type="button"
                                onClick={() => setExpandedMatched(prev => {
                                  const n = new Set(prev);
                                  if (n.has(rid)) n.delete(rid); else n.add(rid);
                                  return n;
                                })}
                                className="text-slate-400 hover:text-slate-700"
                                title={expanded ? 'Hide details' : 'Show match details'}
                                data-testid={`recon-matched-expand-${i}`}
                              >
                                <ChevronsUpDown className="h-3 w-3" />
                              </button>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-sm">
                          <div>{m.recorded.investor_name || m.recorded.investor_id}</div>
                          {(m.recorded.reference || m.recorded.loan_id) && (
                            <div className="text-[10px] text-slate-500 font-mono truncate max-w-[180px]" title={m.recorded.reference || m.recorded.loan_id}>
                              {m.recorded.reference || m.recorded.loan_id}
                            </div>
                          )}
                        </TableCell>
                        <TableCell className="text-sm">{formatDate(m.recorded.transaction_date)}</TableCell>
                        <TableCell><Badge className={getTransactionConfig(m.recorded.transaction_type).color}>{getTransactionConfig(m.recorded.transaction_type).label}</Badge></TableCell>
                        <TableCell className="text-right font-mono text-sm">{formatCurrency(m.recorded.amount)}</TableCell>
                        <TableCell className="text-sm">
                          <div>{formatDate(m.bank.posting_date || m.bank.value_date)}</div>
                          {m.bank.description && (
                            <div className="text-[10px] text-slate-500 truncate max-w-[220px]" title={m.bank.description}>
                              {m.bank.description}
                            </div>
                          )}
                        </TableCell>
                        <TableCell className="text-right font-mono text-sm">
                          {formatCurrency(m.bank.amount)}
                          {existing.length > 1 && (
                            <div className="text-[10px] text-indigo-600">across {existing.length} bank txn(s)</div>
                          )}
                        </TableCell>
                        <TableCell><Badge variant="outline" className={m.match_quality === 'exact' ? 'border-green-500 text-green-700' : m.match_quality === 'verified_no_match' ? 'border-emerald-500 text-emerald-700 bg-emerald-50' : m.match_quality === 'allocated' || m.match_quality === 'manual' ? 'border-indigo-500 text-indigo-700' : 'border-gray-400 text-gray-600'}>{m.match_quality === 'verified_no_match' ? 'Verified (no match)' : m.match_quality}</Badge></TableCell>
                        <TableCell className="flex items-center gap-1">
                          {['allocated', 'manual'].includes(m.match_quality) && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-6 text-xs border-indigo-500 text-indigo-700 hover:bg-indigo-50"
                              onClick={() => handleResplitMatched(m.recorded)}
                              data-testid={`recon-resplit-${i}`}
                            >
                              Re-split
                            </Button>
                          )}
                          <Button variant="ghost" size="sm" className="text-gray-400 hover:text-red-600" onClick={() => {
                            if (m.match_quality === 'verified_no_match') {
                              handleUndoVerifyNoMatch(undoId);
                            } else {
                              handleUnmatch(m.recorded.id, m.bank?.id);
                            }
                          }} data-testid={`recon-unmatch-${i}`}>
                            <X className="h-3 w-3" />
                          </Button>
                        </TableCell>
                      </TableRow>
                      {expanded && (
                        <TableRow data-testid={`recon-matched-detail-${i}`} className="bg-slate-50">
                          <TableCell colSpan={9} className="py-3">
                            <div className="space-y-2 text-xs">
                              {/* Recorded-side metadata */}
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <div className="space-y-1">
                                  <div className="font-semibold text-slate-700">Recorded</div>
                                  <div className="text-slate-600">
                                    <span className="text-slate-400">ID:</span> <span className="font-mono">{m.recorded.id || '—'}</span>
                                  </div>
                                  {m.recorded.loan_id && (
                                    <div className="text-slate-600"><span className="text-slate-400">Loan:</span> <span className="font-mono">{m.recorded.loan_id}</span></div>
                                  )}
                                  {m.recorded.reference && (
                                    <div className="text-slate-600"><span className="text-slate-400">Reference:</span> {m.recorded.reference}</div>
                                  )}
                                  {m.recorded.notes && (
                                    <div className="text-slate-600"><span className="text-slate-400">Notes:</span> {m.recorded.notes}</div>
                                  )}
                                  {m.recorded.source && (
                                    <div className="text-slate-600"><span className="text-slate-400">Source:</span> <Badge className="bg-slate-200 text-[10px]">{m.recorded.source}</Badge></div>
                                  )}
                                </div>
                                <div className="space-y-1">
                                  <div className="font-semibold text-slate-700">Bank</div>
                                  <div className="text-slate-600"><span className="text-slate-400">ID:</span> <span className="font-mono">{m.bank.id || '—'}</span></div>
                                  {m.bank.description && (
                                    <div className="text-slate-600"><span className="text-slate-400">Description:</span> {m.bank.description}</div>
                                  )}
                                  {m.bank.transaction_type && (
                                    <div className="text-slate-600"><span className="text-slate-400">Type:</span> {m.bank.transaction_type}</div>
                                  )}
                                </div>
                              </div>

                              {/* Per-bank allocation breakdown — for multi-bank allocations */}
                              {existing.length > 0 && (
                                <div className="border-t pt-2 mt-2">
                                  <div className="font-semibold text-slate-700 mb-1">Allocation breakdown ({existing.length})</div>
                                  <div className="space-y-1">
                                    {existing.map((ea, j) => (
                                      <div key={j} className={`flex items-center justify-between p-2 rounded border ${ea.kind === 'writeoff' ? 'bg-amber-50 border-amber-200' : 'bg-white border-slate-200'}`} data-testid={`recon-matched-detail-${i}-alloc-${j}`}>
                                        <div className="flex-1 min-w-0">
                                          <div className="font-mono text-[10px] text-slate-500 truncate">
                                            {ea.kind === 'writeoff' ? 'Write-off' : `Bank ${(ea.bank_transaction_id || '').slice(0, 12)}…`}
                                          </div>
                                          <div className="text-slate-600">
                                            {formatDate(ea.effective_date || ea.allocated_at)}
                                            {ea.kind === 'writeoff' && ea.write_off_amount > 0 && (
                                              <span className="ml-2 text-amber-700">(write-off)</span>
                                            )}
                                          </div>
                                        </div>
                                        <div className={`font-mono font-semibold ${ea.kind === 'writeoff' ? 'text-amber-700' : 'text-indigo-700'}`}>
                                          {formatCurrency(ea.kind === 'writeoff' ? (ea.write_off_amount || ea.allocated_amount) : ea.allocated_amount)}
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}

                              {/* Verify-no-match reason */}
                              {m.match_quality === 'verified_no_match' && (
                                <div className="border-t pt-2 mt-2">
                                  <div className="font-semibold text-slate-700">Verified reason</div>
                                  <div className="text-slate-600 italic">
                                    {m.recorded.verified_no_match_reason || m.bank.verified_no_match_reason || 'Adjustment — no counterpart required'}
                                  </div>
                                  {m.full_amount !== undefined && m.verified_amount !== undefined && Math.abs(Number(m.full_amount) - Number(m.verified_amount)) > 0.01 && (
                                    <div className="mt-2 text-slate-700 bg-emerald-50 border border-emerald-200 rounded px-2 py-1 text-[11px]">
                                      <span className="font-semibold">Partial verify</span> — original amount was <span className="font-mono">{formatCurrency(m.full_amount)}</span>, of which <span className="font-mono">{formatCurrency(Number(m.full_amount) - Number(m.verified_amount))}</span> was allocated and only the remaining <span className="font-mono">{formatCurrency(m.verified_amount)}</span> was verified as no-match.
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                      </React.Fragment>
                    );
                    })}
                  </TableBody>
                </Table>
                ); })()}
              </CardContent>
              )}
            </Card>
          )}

          {/* Suggested Matches — fuzzy 1-to-1 + client-code sum. Admin must Accept/Reject. */}
          {(reconData.suggestions || []).length > 0 && (
            <Card className="border-blue-200 bg-blue-50/30">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between gap-2">
                  <button
                    type="button"
                    onClick={() => setShowSuggestions(s => !s)}
                    className="flex-1 flex items-center justify-between text-sm font-semibold text-blue-800 hover:text-blue-900 transition"
                    data-testid="recon-suggestions-toggle"
                  >
                    <span className="flex items-center gap-2">
                      <AlertTriangle className="h-4 w-4 text-blue-600" />
                      Suggested Matches ({(reconData.suggestions || []).length})
                      <span className="text-xs text-blue-600 font-normal italic">— review + accept or reject</span>
                    </span>
                    <span className="flex items-center gap-1 text-xs text-blue-600">
                      {showSuggestions ? 'Hide' : 'Show'}
                      <ChevronsUpDown className="h-3 w-3" />
                    </span>
                  </button>
                </div>
                <CardDescription className="text-xs text-blue-700">
                  Non-exact potential matches (loan/client code in description, amount within 10c, or client-code sum). Nothing is committed until you click <b>Accept</b>. <b>Reject</b> silences the suggestion permanently. Select multiple rows to accept/reject them together.
                </CardDescription>
              </CardHeader>
              {showSuggestions && (
                <CardContent className="p-0">
                  {selectedSuggestions.size > 0 && (
                    <div className="flex items-center gap-2 px-3 py-2 bg-blue-100/60 border-b border-blue-200">
                      <span className="text-xs font-medium text-blue-800">{selectedSuggestions.size} selected</span>
                      <Button
                        size="sm"
                        onClick={bulkAcceptSuggestions}
                        disabled={bulkSuggestionLoading}
                        className="bg-green-600 hover:bg-green-700 text-white"
                        data-testid="suggestion-bulk-accept-btn"
                      >
                        {bulkSuggestionLoading ? 'Working…' : `Accept ${selectedSuggestions.size}`}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={bulkRejectSuggestions}
                        disabled={bulkSuggestionLoading}
                        className="border-red-300 text-red-700 hover:bg-red-50"
                        data-testid="suggestion-bulk-reject-btn"
                      >
                        {bulkSuggestionLoading ? 'Working…' : `Reject ${selectedSuggestions.size}`}
                      </Button>
                    </div>
                  )}
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="text-xs w-8">
                          <input
                            type="checkbox"
                            checked={selectedSuggestions.size > 0 && selectedSuggestions.size === (reconData.suggestions || []).length}
                            onChange={() => toggleAllSuggestionsSelected(
                              (reconData.suggestions || [])
                                .map(s => `${s.bank?.id}::${(s.recorded || []).map(r => r.id).filter(Boolean).join(',')}`)
                            )}
                            data-testid="suggestion-select-all"
                          />
                        </TableHead>
                        <TableHead className="text-xs">Bank line</TableHead>
                        <TableHead className="text-xs">Recorded row(s)</TableHead>
                        <TableHead className="text-xs">Why</TableHead>
                        <TableHead className="text-xs text-center">Confidence</TableHead>
                        <TableHead className="text-xs text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(reconData.suggestions || []).map((s, idx) => {
                        const bank = s.bank || {};
                        const recs = s.recorded || [];
                        const key = `${bank.id}::${recs.map(r => r.id).join(',')}`;
                        const busy = suggestionBusyId === key;
                        const confColor = s.confidence === 'high' ? 'border-green-500 text-green-700'
                          : s.confidence === 'medium' ? 'border-amber-500 text-amber-700'
                          : 'border-slate-400 text-slate-700';
                        return (
                          <TableRow key={key + idx} data-testid={`suggestion-row-${idx}`}>
                            <TableCell className="align-top pt-3">
                              <input
                                type="checkbox"
                                checked={selectedSuggestions.has(key)}
                                onChange={() => toggleSuggestionSelected(key)}
                                data-testid={`suggestion-select-${idx}`}
                              />
                            </TableCell>
                            <TableCell className="text-xs align-top">
                              <div className="font-mono">{formatCurrency(bank.amount)}</div>
                              <div className="text-slate-500">{bank.posting_date?.slice(0, 10) || '—'}</div>
                              <div className="text-slate-500 max-w-[220px] truncate" title={bank.description || bank.bank_description}>
                                {bank.description || bank.bank_description || '—'}
                              </div>
                            </TableCell>
                            <TableCell className="text-xs align-top">
                              {recs.map((r, i) => (
                                <div key={i} className="mb-1">
                                  <div className="font-mono">{formatCurrency(r.amount)}</div>
                                  <div className="text-slate-500">
                                    {r.transaction_date?.slice(0, 10) || '—'} · {r.transaction_type || '—'}
                                  </div>
                                  {r.loan_id && <div className="text-slate-500 font-mono text-[10px]">{r.loan_id}</div>}
                                </div>
                              ))}
                            </TableCell>
                            <TableCell className="text-xs align-top max-w-[280px]">
                              <div className="italic text-slate-700">{s.reason}</div>
                              {(() => {
                                // Date used if matched = the UPLOADED row's own date (user
                                // 2026-10-16); the bank date never replaces it. Shown only
                                // when the dates differ.
                                const bd = bank.posting_date?.slice(0, 10);
                                const recDates = [...new Set(recs.map(r => r.transaction_date?.slice(0, 10)).filter(Boolean))];
                                if (!bd || !recDates.length || recDates.every(d => d === bd)) return null;
                                return (
                                  <div className="mt-1 rounded bg-blue-50 border border-blue-200 px-2 py-1 text-blue-900"
                                    data-testid={`suggestion-date-used-${idx}`}>
                                    Date used if matched: <b>{recDates.join(', ')}</b> (uploaded)
                                    {recDates.length > 1 && <span> · each row keeps its own date</span>}
                                    <div className="text-blue-700">Bank date {bd} is not used</div>
                                  </div>
                                );
                              })()}
                              <div className="text-slate-500 mt-1">
                                kind: <span className="font-mono">{s.kind}</span>
                                {' · '}on: <span className="font-mono">{s.matched_on}</span>
                              </div>
                            </TableCell>
                            <TableCell className="text-xs text-center align-top">
                              <Badge variant="outline" className={confColor}>{s.confidence}</Badge>
                            </TableCell>
                            <TableCell className="text-xs text-right align-top">
                              <div className="flex flex-col gap-1 items-end">
                                <Button
                                  size="sm"
                                  onClick={() => acceptSuggestion(s)}
                                  disabled={busy}
                                  className="bg-green-600 hover:bg-green-700 text-white"
                                  data-testid={`suggestion-accept-${idx}`}
                                >
                                  {busy ? '…' : 'Accept'}
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => rejectSuggestion(s)}
                                  disabled={busy}
                                  className="border-red-300 text-red-700 hover:bg-red-50"
                                  data-testid={`suggestion-reject-${idx}`}
                                >
                                  {busy ? '…' : 'Reject'}
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </CardContent>
              )}
            </Card>
          )}

          {/* Unmatched Recorded Transactions */}
          {reconData.unmatched_recorded.length > 0 && (
            <Card>
              <CardHeader className="pb-2 sticky top-16 z-30 bg-white border-b shadow-sm">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => setShowUnmatchedUploaded(s => !s)}
                    className="flex-1 flex items-center gap-2 text-left"
                    data-testid="recon-unmatched-uploaded-toggle"
                  >
                    <CardTitle className="text-sm flex items-center gap-2 hover:text-slate-900 transition"><AlertTriangle className="h-4 w-4 text-amber-600" /> Unmatched Uploaded Transactions ({filteredRecorded.length}{(_q || _fromDt || _toDt) && filteredRecorded.length !== reconData.unmatched_recorded.length ? ` of ${reconData.unmatched_recorded.length}` : ''})</CardTitle>
                    <span className="flex items-center gap-1 text-xs text-slate-500 ml-2">
                      {showUnmatchedUploaded ? 'Hide' : 'Show'}
                      <ChevronsUpDown className="h-3 w-3" />
                    </span>
                  </button>
                  {selectedReconRecorded.size > 0 && (() => {
                    // Running tally of selected uploaded rows. Preserves signs — does NOT flip negatives.
                    // (User spec: "show them as they are".) A row with a prior partial
                    // allocation carries `remaining_to_allocate` (the unsigned amount still
                    // outstanding) - use that magnitude instead of the original full amount,
                    // which otherwise overstates the tally for a row that's already partly spoken for.
                    const tally = (reconData.unmatched_recorded || [])
                      .filter(t => selectedReconRecorded.has(t.id))
                      .reduce((acc, t) => {
                        const amt = parseFloat(t.amount) || 0;
                        const sign = amt < 0 ? -1 : 1;
                        const magnitude = t.remaining_to_allocate != null ? Math.abs(Number(t.remaining_to_allocate)) : Math.abs(amt);
                        return acc + sign * magnitude;
                      }, 0);
                    const tallyStr = Number(tally).toLocaleString('en-ZA', {
                      minimumFractionDigits: 2, maximumFractionDigits: 2, signDisplay: 'always',
                    });
                    const tallyColor = tally > 0 ? 'text-emerald-700 bg-emerald-50 border-emerald-300'
                                      : tally < 0 ? 'text-red-700 bg-red-50 border-red-300'
                                      : 'text-slate-700 bg-slate-50 border-slate-300';
                    return (
                      <div className={`flex items-center gap-1.5 px-3 py-1 rounded-md border text-sm font-mono ${tallyColor}`} data-testid="recon-selected-running-tally">
                        <span className="text-xs uppercase tracking-wide opacity-70">Σ {selectedReconRecorded.size} selected</span>
                        <span className="font-semibold">R {tallyStr}</span>
                      </div>
                    );
                  })()}
                  {selectedReconRecorded.size > 0 && (
                    <>
                      <Button variant="outline" size="sm" onClick={handleReconBulkVerifyRecorded} disabled={reconBulkDeleting} className="text-emerald-700 border-emerald-300 hover:bg-emerald-50" data-testid="recon-bulk-verify-recorded">
                        <CheckCircle className="h-3 w-3 mr-1" />Verify {selectedReconRecorded.size} Selected
                      </Button>
                      <Button variant="destructive" size="sm" onClick={handleReconBulkDeleteRecorded} disabled={reconBulkDeleting} data-testid="recon-bulk-delete-recorded">
                        <Trash2 className="h-3 w-3 mr-1" />{reconBulkDeleting ? 'Working...' : `Delete ${selectedReconRecorded.size} Selected`}
                      </Button>
                      <Button variant="destructive" size="sm" onClick={handleReconBulkForceCleanRecorded} disabled={reconBulkDeleting} data-testid="recon-bulk-force-clean-recorded">
                        <Trash2 className="h-3 w-3 mr-1" />{reconBulkDeleting ? 'Working...' : `Force-Clean ${selectedReconRecorded.size} Selected`}
                      </Button>
                    </>
                  )}
                  {/* Always-on Dedupe Uploaded action — scoped to the
                      current investor filter when one is selected. */}
                  <Button
                    variant="outline"
                    size="sm"
                    className="border-amber-300 text-amber-700 hover:bg-amber-50"
                    data-testid="recon-dedupe-uploaded-button"
                    title="Find and remove duplicate uploaded rows across matched + unmatched sections. Rule: same date + same type + same amount = duplicate. Allocated/verified rows are preserved as survivors."
                    onClick={() => runDedupe('uploaded')}
                  >
                    <Trash2 className="h-4 w-4 mr-1" /> Remove Uploaded Duplicates
                  </Button>
                </div>
                <CardDescription>Click a row to select it for manual matching, or use checkboxes for bulk delete. The running tally above sums the selected rows as-is (signs preserved) — useful for same-day rebalance net checks.</CardDescription>
              </CardHeader>
              {showUnmatchedUploaded && (
              <CardContent>
                <Table>
                  <TableHeader><TableRow className="bg-amber-50"><TableHead className="w-10"><input type="checkbox" checked={filteredRecorded.length > 0 && selectedReconRecorded.size === filteredRecorded.length} onChange={() => toggleAllReconRecorded(filteredRecorded)} className="rounded" /></TableHead>
                    <SortableHeader label="Investor" sortKey="investor_name" sortState={sortReconRecorded} onSort={toggleSort(setSortReconRecorded)} />
                    <SortableHeader label="Date" sortKey="transaction_date" sortState={sortReconRecorded} onSort={toggleSort(setSortReconRecorded)} />
                    <SortableHeader label="Type" sortKey="transaction_type" sortState={sortReconRecorded} onSort={toggleSort(setSortReconRecorded)} />
                    <SortableHeader label="Amount" sortKey="amount" sortState={sortReconRecorded} onSort={toggleSort(setSortReconRecorded)} className="text-right" />
                    <SortableHeader label="Reference" sortKey="reference" sortState={sortReconRecorded} onSort={toggleSort(setSortReconRecorded)} />
                    <TableHead></TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {filteredRecorded.map((txn, i) => {
                      const isSelected = selectedRecordedTxn?.id === txn.id;
                      const hasAlloc = txn.allocated_amount > 0;
                      return (
                        <TableRow key={i}
                          data-testid={`recon-unmatched-recorded-${i}`}
                          className={`cursor-pointer transition-colors ${isSelected ? 'bg-blue-100 border-blue-300' : hasAlloc ? 'bg-indigo-50' : selectedReconRecorded.has(txn.id) ? 'bg-amber-50' : 'hover:bg-amber-50'}`}
                          onClick={() => setSelectedRecordedTxn(isSelected ? null : txn)}
                        >
                          <TableCell onClick={e => e.stopPropagation()}><input type="checkbox" checked={selectedReconRecorded.has(txn.id)} onChange={() => toggleReconRecorded(txn.id)} className="rounded" /></TableCell>
                          <TableCell className="text-sm">
                            {(txn.source === 'RP' || txn.source === 'LB') && (
                              <Badge className="bg-purple-100 text-purple-800 text-[10px] mr-1 align-middle" data-testid={`recon-source-badge-${i}`}>{txn.source}</Badge>
                            )}
                            {txn.source === 'staging' && (
                              <Badge className="bg-yellow-100 text-yellow-800 text-[10px] mr-1 align-middle">Pending</Badge>
                            )}
                            {txn.split_kind === 'non_cash' && (
                              <Badge className="bg-indigo-100 text-indigo-800 text-[10px] mr-1 align-middle" title="Non-cash part of a split — reconcile it against the uploaded transaction it offsets">
                                Non-cash (split)
                              </Badge>
                            )}
                            {txn.possible_duplicate_count > 1 && (
                              <Badge
                                className="bg-orange-100 text-orange-800 border border-orange-300 text-[10px] mr-1 align-middle"
                                title={`The LB/RP workbook lists this cashflow ${txn.possible_duplicate_count} times on the same loan row (same date and amount). It may be a genuine repeat payment or a duplicate in the workbook. To remove a duplicate, clear the repeated cell in the workbook and re-upload LB/RP.`}
                                data-testid={`recon-possible-duplicate-${i}`}
                              >
                                Possible duplicate ×{txn.possible_duplicate_count}
                              </Badge>
                            )}
                            {(!txn.source || txn.source === 'manual' || txn.source === 'csv') && (
                              <Badge className="bg-slate-100 text-slate-700 text-[10px] mr-1 align-middle">CSV</Badge>
                            )}
                            {txn.investor_name || txn.investor_id}
                          </TableCell>
                          <TableCell className="text-sm">{formatDate(txn.transaction_date)}</TableCell>
                          <TableCell><Badge className={getTransactionConfig(txn.transaction_type).color}>{getTransactionConfig(txn.transaction_type).label}</Badge></TableCell>
                          <TableCell className="text-right font-mono text-sm">{formatCurrency(txn.amount)}</TableCell>
                          <TableCell className="text-sm">
                            {hasAlloc ? (
                              <div className="space-y-0.5">
                                <div className="text-xs text-indigo-700 font-medium">Allocated: {formatCurrency(txn.allocated_amount)}</div>
                                <div className="text-xs text-red-600">Remaining: {formatCurrency((parseFloat(txn.amount) < 0 ? -1 : 1) * Math.abs(Number(txn.remaining_to_allocate) || 0))}</div>
                                <div className="flex items-center gap-2">
                                  <button
                                    onClick={(e) => { e.stopPropagation(); handleOpenReverseAllocation(txn); }}
                                    className="text-xs text-indigo-600 underline hover:text-indigo-800 cursor-pointer"
                                    data-testid={`recon-recorded-inspect-${i}`}
                                  >
                                    Inspect & adjust
                                  </button>
                                  <button
                                    onClick={(e) => { e.stopPropagation(); openInspectRecorded(txn.id); }}
                                    className="text-xs text-rose-700 underline hover:text-rose-900 cursor-pointer"
                                    data-testid={`recon-recorded-diagnose-${i}`}
                                    title="Diagnostic view — list every allocation record on this recorded txn, with force-clean for phantoms"
                                  >
                                    Diagnose
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <span className="text-gray-400">{txn.reference || '-'}</span>
                            )}
                          </TableCell>
                          <TableCell className="flex items-center gap-1">
                            {isSelected && <Badge className="bg-blue-100 text-blue-700">Selected</Badge>}
                            {hasAlloc && <Badge className="bg-indigo-100 text-indigo-700 text-xs">Partial</Badge>}
                            {isSelected && (
                              <Button
                                size="sm"
                                className="h-6 text-xs bg-indigo-600 hover:bg-indigo-700 text-white"
                                onClick={(e) => { e.stopPropagation(); handleOpenReverseAllocation(txn); }}
                                data-testid={`recon-allocate-banks-btn-${i}`}
                              >
                                Allocate Bank(s)
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-6 px-2 text-xs text-indigo-700 hover:bg-indigo-50"
                              title="Split this into multiple parts (e.g. cash + non-cash)"
                              onClick={(e) => { e.stopPropagation(); handleOpenSplit(txn); }}
                              data-testid={`recon-split-recorded-${i}`}
                            >
                              Split
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-6 px-2 text-xs text-emerald-700 hover:bg-emerald-50"
                              title="Verify this as a non-cash adjustment (no bank match required)"
                              onClick={(e) => { e.stopPropagation(); handleVerifyNoMatch(txn, 'recorded'); }}
                              data-testid={`recon-verify-no-match-recorded-${i}`}
                            >
                              Verify
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-6 px-2 text-xs text-rose-700 hover:bg-rose-50"
                              title="Diagnose — see every allocation record (incl. phantoms) on this recorded txn and surgically remove them"
                              onClick={(e) => { e.stopPropagation(); openInspectRecorded(txn.id); }}
                              data-testid={`recon-diagnose-recorded-${i}`}
                            >
                              Diagnose
                            </Button>
                            <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-gray-400 hover:text-red-600" onClick={async (e) => { e.stopPropagation(); if (!window.confirm('Delete this transaction?')) return; try { await axios.delete(`${API_URL}/api/admin/investor-transactions/${txn.id}`, { headers }); toast.success('Deleted'); fetchReconciliation(reconInvestorFilter === 'all' ? '' : reconInvestorFilter, reconFromDate, reconToDate); } catch { toast.error('Delete failed'); } }} data-testid={`recon-delete-recorded-${i}`}><Trash2 className="h-3 w-3" /></Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </CardContent>
              )}
            </Card>
          )}

          {/* Unmatched Bank Transactions */}
          {/* Unmatched Bank */}
          {(reconData.unmatched_bank || []).length > 0 && (
            <Card>
              <CardHeader className="pb-2 sticky top-16 z-30 bg-white border-b shadow-sm">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => setShowUnmatchedBank(s => !s)}
                    className="flex-1 flex items-center gap-2 text-left"
                    data-testid="recon-unmatched-bank-toggle"
                  >
                    <CardTitle className="text-sm flex items-center gap-2 hover:text-slate-900 transition"><XCircle className="h-4 w-4 text-red-600" /> Unmatched Bank Movements ({filteredBank.length}{(_q || _fromDt || _toDt) && filteredBank.length !== reconData.unmatched_bank.length ? ` of ${reconData.unmatched_bank.length}` : ''})</CardTitle>
                    <span className="flex items-center gap-1 text-xs text-slate-500 ml-2">
                      {showUnmatchedBank ? 'Hide' : 'Show'}
                      <ChevronsUpDown className="h-3 w-3" />
                    </span>
                  </button>
                  {selectedReconBank.size > 0 && (() => {
                    // Same running tally for bank selections — signs preserved as-is.
                    // A bank row with a prior partial allocation carries `remaining_balance`
                    // (unsigned) - use that magnitude instead of the original full amount.
                    const tally = (reconData.unmatched_bank || [])
                      .filter(b => selectedReconBank.has(b.id))
                      .reduce((acc, b) => {
                        const amt = parseFloat(b.amount) || 0;
                        const sign = amt < 0 ? -1 : 1;
                        const magnitude = b.remaining_balance != null ? Math.abs(Number(b.remaining_balance)) : Math.abs(amt);
                        return acc + sign * magnitude;
                      }, 0);
                    const tallyStr = Number(tally).toLocaleString('en-ZA', {
                      minimumFractionDigits: 2, maximumFractionDigits: 2, signDisplay: 'always',
                    });
                    const tallyColor = tally > 0 ? 'text-emerald-700 bg-emerald-50 border-emerald-300'
                                      : tally < 0 ? 'text-red-700 bg-red-50 border-red-300'
                                      : 'text-slate-700 bg-slate-50 border-slate-300';
                    return (
                      <div className={`flex items-center gap-1.5 px-3 py-1 rounded-md border text-sm font-mono ${tallyColor}`} data-testid="recon-bank-selected-running-tally">
                        <span className="text-xs uppercase tracking-wide opacity-70">Σ {selectedReconBank.size} selected</span>
                        <span className="font-semibold">R {tallyStr}</span>
                      </div>
                    );
                  })()}
                  {selectedReconBank.size > 0 && (
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" disabled={reconBulkDeleting} onClick={() => setReassignOpen(true)} className="text-indigo-700 border-indigo-300 hover:bg-indigo-50" data-testid="recon-bulk-reassign-bank">
                        <Users className="h-4 w-4 mr-1" /> Reassign {selectedReconBank.size} to Investor
                      </Button>
                      <Button variant="outline" size="sm" disabled={reconBulkDeleting} onClick={handleReconBulkVerifyBank} className="text-emerald-700 border-emerald-300 hover:bg-emerald-50" data-testid="recon-bulk-verify-bank">
                        <CheckCircle className="h-4 w-4 mr-1" /> Verify {selectedReconBank.size} Selected
                      </Button>
                      <Button variant="destructive" size="sm" disabled={reconBulkDeleting} onClick={handleReconBulkDeleteBank} data-testid="recon-bulk-delete-bank">
                        <Trash2 className="h-4 w-4 mr-1" /> Delete {selectedReconBank.size} Selected
                      </Button>
                      <Button variant="destructive" size="sm" disabled={reconBulkDeleting} onClick={handleReconBulkForceCleanBank} data-testid="recon-bulk-force-clean-bank">
                        <Trash2 className="h-4 w-4 mr-1" /> Force-Clean {selectedReconBank.size} Selected
                      </Button>
                    </div>
                  )}
                  {/* Always-on Dedupe Bank action — scoped to the
                      current investor filter when one is selected. */}
                  <Button
                    variant="outline"
                    size="sm"
                    className="border-amber-300 text-amber-700 hover:bg-amber-50"
                    data-testid="recon-dedupe-bank-button"
                    title="Find and remove duplicate bank rows (across both Matched and Unmatched sections). Allocated/verified rows are preserved as survivors."
                    onClick={() => runDedupe('bank')}
                  >
                    <Trash2 className="h-4 w-4 mr-1" /> Remove Bank Duplicates
                  </Button>
                </div>
                <CardDescription>Click a row to select it for manual matching, or tick checkboxes to bulk-verify (no-counterpart) or bulk-delete. Σ tally above sums selected rows as-is (signs preserved) — useful for net-rebalance checks.</CardDescription>
              </CardHeader>
              {showUnmatchedBank && (
              <CardContent>
                <Table>
                  <TableHeader><TableRow className="bg-red-50">
                    <TableHead className="w-8"><input type="checkbox" checked={selectedReconBank.size === filteredBank.length && filteredBank.length > 0} onChange={() => toggleAllReconBank(filteredBank)} data-testid="recon-bank-select-all" /></TableHead>
                    <SortableHeader label="Date" sortKey="posting_date" sortState={sortReconBank} onSort={toggleSort(setSortReconBank)} />
                    <SortableHeader label="Description" sortKey="description" sortState={sortReconBank} onSort={toggleSort(setSortReconBank)} />
                    <SortableHeader label="Type" sortKey="transaction_type" sortState={sortReconBank} onSort={toggleSort(setSortReconBank)} />
                    <SortableHeader label="Amount" sortKey="amount" sortState={sortReconBank} onSort={toggleSort(setSortReconBank)} className="text-right" />
                    <TableHead></TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {filteredBank.map((b, i) => {
                      const isSelected = selectedBankTxn?.id === b.id;
                      const isChecked = selectedReconBank.has(b.id);
                      return (
                        <TableRow key={i}
                          data-testid={`recon-unmatched-bank-${i}`}
                          className={`cursor-pointer transition-colors ${isSelected ? 'bg-blue-100 border-blue-300' : isChecked ? 'bg-red-50' : 'hover:bg-red-50'}`}
                          onClick={() => setSelectedBankTxn(isSelected ? null : b)}
                        >
                          <TableCell onClick={e => e.stopPropagation()}><input type="checkbox" checked={isChecked} onChange={() => toggleReconBank(b.id)} data-testid={`recon-bank-check-${i}`} /></TableCell>
                          <TableCell className="text-sm">{formatDate(b.posting_date || b.value_date)}</TableCell>
                          <TableCell className="text-sm">
                            {b.description || '-'}
                            {b.split_parent_id && (
                              <div className="text-[10px] mt-0.5 inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-amber-50 text-amber-800 border border-amber-200">
                                Split {b.split_index}/{b.split_of} of {formatCurrency(b.split_parent_amount)}
                              </div>
                            )}
                          </TableCell>
                          <TableCell>{b.transaction_type ? <Badge variant="outline">{b.transaction_type}</Badge> : <span className="text-gray-400">-</span>}</TableCell>
                          <TableCell className="text-right font-mono text-sm">
                            {formatCurrency(b.amount)}
                            {b.remaining_balance != null && b.allocated_amount > 0 && (
                              <div className="text-xs font-normal mt-0.5">
                                <span className="text-indigo-700">Alloc: {formatCurrency(b.allocated_amount)}</span>
                                <span className="text-red-600 ml-2">Rem: {formatCurrency(b.remaining_balance)}</span>
                                <button
                                  onClick={(e) => { e.stopPropagation(); openInspect(b.id); }}
                                  className="ml-2 text-indigo-600 underline hover:text-indigo-800 cursor-pointer"
                                  data-testid={`recon-bank-inspect-${i}`}
                                >
                                  Inspect
                                </button>
                              </div>
                            )}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1">
                              {isSelected && (
                                <>
                                  <Badge className="bg-blue-100 text-blue-700">Selected</Badge>
                                  <Button
                                    variant="default"
                                    size="sm"
                                    className="h-6 text-xs bg-green-600 hover:bg-green-700 text-white"
                                    onClick={(e) => { e.stopPropagation(); handleOpenAllocation(b); }}
                                    data-testid={`recon-allocate-btn-${i}`}
                                  >
                                    Allocate
                                  </Button>
                                </>
                              )}
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-6 px-2 text-xs text-emerald-700 hover:bg-emerald-50"
                                title="Verify this bank txn as a cash adjustment (no uploaded match required)"
                                onClick={(e) => { e.stopPropagation(); handleVerifyNoMatch(b, 'bank'); }}
                                data-testid={`recon-verify-no-match-bank-${i}`}
                              >
                                Verify
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-6 px-2 text-xs text-amber-700 hover:bg-amber-50"
                                title={b.split_parent_id
                                  ? 'This row is already a split child'
                                  : 'Split this bank txn into multiple children (each individually matchable)'}
                                disabled={!!b.split_parent_id || (b.allocated_amount > 0)}
                                onClick={(e) => { e.stopPropagation(); handleOpenBankSplit(b); }}
                                data-testid={`recon-split-bank-${i}`}
                              >
                                Split
                              </Button>
                              {b.split_parent_id && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-6 px-2 text-xs text-amber-800 hover:bg-amber-100"
                                  title="Unsplit — delete all children and restore the parent"
                                  onClick={(e) => { e.stopPropagation(); handleUnsplitBank(b.id); }}
                                  data-testid={`recon-unsplit-bank-${i}`}
                                >
                                  Unsplit
                                </Button>
                              )}
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-6 px-2 text-xs text-rose-700 hover:bg-rose-50"
                                title="Diagnose — see every allocation record (incl. phantoms) on this bank txn and force-clean if stuck"
                                onClick={(e) => { e.stopPropagation(); openInspect(b.id); }}
                                data-testid={`recon-diagnose-bank-${i}`}
                              >
                                Diagnose
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </CardContent>
              )}
            </Card>
          )}

          {/* Allocation History — removed; matched section at top serves the same purpose */}

          {reconData.summary.total_recorded === 0 && reconData.summary.total_bank === 0 && (
            <Card><CardContent className="py-12 text-center"><FileText className="h-10 w-10 text-gray-300 mx-auto mb-3" /><p className="text-gray-500">No transaction data to reconcile.</p><p className="text-sm text-gray-400 mt-1">Upload transactions via the Import tab or sync bank data from Investec.</p></CardContent></Card>
          )}
        </>
      )}
      </>
        );
      })()}

      {/* Forensic audit trail of auto-matches */}
      <AutoMatchAuditPanel
        API_URL={API_URL}
        headers={headers}
        investorFilter={reconInvestorFilter}
      />
    </TabsContent>
  );
}


// ─── Bulk-edit transaction_type control ────────────────────────────────────
//
// Renders inside the sticky selection bar when ANY of the three selection
// sets (unmatched recorded, unmatched bank, matched pair) has items. Uses
// a native <select> to avoid dragging in extra shadcn wiring.
function BulkEditTypeControl({
  ctx,
  selectedReconRecorded,
  selectedReconBank,
  selectedMatched,
  fetchReconciliation,
  reconInvestorFilter,
  reconFromDate,
  reconToDate,
  setSelectedReconRecorded,
  setSelectedReconBank,
  setSelectedMatched,
}) {
  const [newType, setNewType] = React.useState('');
  const [busy, setBusy] = React.useState(false);

  const totalSelected =
    selectedReconRecorded.size + selectedReconBank.size + selectedMatched.size;

  const apply = async () => {
    if (!newType) {
      toast.error('Pick a transaction type first');
      return;
    }
    if (totalSelected === 0) {
      toast.error('Select at least one row');
      return;
    }
    // Deduplicate ids across the three sets (matched rows can share an id
    // with their recorded counterpart).
    const ids = Array.from(new Set([
      ...selectedReconRecorded,
      ...selectedReconBank,
      ...selectedMatched,
    ]));
    setBusy(true);
    // Only the mutation itself can fail this action — a hiccup in the
    // post-success refresh/cleanup below must never get mislabeled as
    // "the type change failed" when the server already applied it.
    let res;
    try {
      res = await axios.post(
        `${API_URL}/api/admin/investor-transactions/reconciliation/bulk-edit-type`,
        { ids, transaction_type: newType },
        { headers: ctx.headers },
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Bulk type update failed');
      setBusy(false);
      return;
    }

    const d = res.data || {};
    const updated = d.total_updated ?? 0;
    const missing = (d.not_found || []).length;
    if (updated > 0) {
      toast.success(
        `Type changed to '${d.new_type}' on ${updated} row${updated === 1 ? '' : 's'}` +
        (missing ? ` (${missing} not found)` : ''),
      );
    } else {
      toast.error(`No rows updated${missing ? ` (${missing} not found)` : ''}`);
    }
    setSelectedReconRecorded(new Set());
    setSelectedReconBank(new Set());
    setSelectedMatched(new Set());
    setBusy(false);
    // fetchReconciliation reports its own failures (a distinct, correctly
    // labeled toast) — don't let that surface as a bulk-edit-type failure.
    // Must pass the active filters: the backend rejects an unfiltered
    // "all investors" reconciliation load with 400 (see the GET route),
    // and calling this with no args was tripping that guard every time.
    fetchReconciliation(reconInvestorFilter === 'all' ? '' : reconInvestorFilter, reconFromDate, reconToDate);
  };

  return (
    <div className="flex items-center gap-2" data-testid="recon-bulk-edit-type-control">
      <select
        value={newType}
        onChange={(e) => setNewType(e.target.value)}
        className="text-xs h-8 rounded border border-slate-300 bg-white px-2 focus:outline-none focus:ring-2 focus:ring-emerald-500"
        data-testid="recon-bulk-edit-type-select"
      >
        <option value="">Change type…</option>
        {TRANSACTION_TYPES.map((t) => (
          <option key={t.value} value={t.value}>
            {t.label}
          </option>
        ))}
      </select>
      <Button
        variant="outline"
        size="sm"
        onClick={apply}
        disabled={busy || !newType || totalSelected === 0}
        className="border-indigo-300 text-indigo-700 hover:bg-indigo-50 h-8"
        data-testid="recon-bulk-edit-type-apply"
        title={`Change type on ${totalSelected} selected row${totalSelected === 1 ? '' : 's'}`}
      >
        {busy ? 'Applying…' : `Apply to ${totalSelected}`}
      </Button>
    </div>
  );
}
